import { trackSchemaEvent } from "metabase/analytics";
import type { ExportFormat } from "metabase/common/types/export";
import type {
  EmbedResourceDownloadOptions,
  EmbeddingParameterVisibility,
} from "metabase-types/api";

import type {
  BuiltInDisplayTheme,
  DisplayTheme,
  EmbedResource,
  EmbedResourceType,
  EmbeddingDisplayOptions,
} from "../types";

const SCHEMA_NAME = "embed_flow";

// We changed the UI to `Look and Feel` now
type Appearance = {
  background: boolean;
  titled: boolean;
  bordered: boolean;
  theme: BuiltInDisplayTheme;
  font: "instance" | "custom";
  enabled_download_types: EmbedResourceDownloadOptions | null;

  /** @deprecated use `enabled_download_types` instead */
  downloads: boolean | null;
};

const BUILT_IN_DISPLAY_THEMES: BuiltInDisplayTheme[] = [
  "light",
  "night",
  "transparent",
];

/**
 * The `embed_flow` snowplow schema constrains `theme` to the built-in themes,
 * so the slug of a saved theme cannot be reported as-is without failing schema
 * validation. Saved themes are reported as `light`; the alternative would be a
 * new schema version with an unbounded string.
 */
function normalizeTheme(theme: DisplayTheme | null): BuiltInDisplayTheme {
  return BUILT_IN_DISPLAY_THEMES.find((builtIn) => builtIn === theme) ?? "light";
}

export const trackStaticEmbedDiscarded = ({
  artifact,
}: {
  artifact: EmbedResourceType;
}): void => {
  trackSchemaEvent(SCHEMA_NAME, {
    event: "static_embed_discarded",
    artifact,
  });
};

export const trackStaticEmbedPublished = ({
  artifact,
  resource,
  params,
  isExampleDashboard,
}: {
  artifact: EmbedResourceType;
  resource: EmbedResource;
  params: Record<EmbeddingParameterVisibility, number>;
  isExampleDashboard: boolean;
}): void => {
  const now = Date.now();
  trackSchemaEvent(SCHEMA_NAME, {
    event: "static_embed_published",
    artifact,
    new_embed: !resource.initially_published_at,
    time_since_creation: toSecond(
      now - new Date(resource.created_at).getTime(),
    ),
    time_since_initial_publication: resource.initially_published_at
      ? toSecond(now - new Date(resource.initially_published_at).getTime())
      : null,
    params,
    is_example_dashboard: isExampleDashboard,
  });
};

function toSecond(milliseconds: number) {
  return Math.round(milliseconds / 1000);
}

export const trackStaticEmbedUnpublished = ({
  artifact,
  resource,
}: {
  artifact: EmbedResourceType;
  resource: EmbedResource;
}): void => {
  const now = Date.now();
  trackSchemaEvent(SCHEMA_NAME, {
    event: "static_embed_unpublished",
    artifact,
    time_since_creation: toSecond(
      now - new Date(resource.created_at).getTime(),
    ),
    time_since_initial_publication: resource.initially_published_at
      ? toSecond(now - new Date(resource.initially_published_at).getTime())
      : null,
  });
};

export const trackStaticEmbedCodeCopied = ({
  artifact,
  language,
  location,
  code,
  displayOptions,
}: {
  artifact: EmbedResourceType;
  language: string;
  location: "code_overview" | "code_params" | "code_appearance";
  code: "backend" | "view";
  displayOptions: EmbeddingDisplayOptions;
}): void => {
  trackSchemaEvent(SCHEMA_NAME, {
    event: "static_embed_code_copied",
    artifact,
    language,
    location,
    code,
    appearance: normalizeAppearance(displayOptions),
  });
};

function normalizeAppearance(
  displayOptions: EmbeddingDisplayOptions,
): Appearance {
  return {
    background: displayOptions.background,
    titled: displayOptions.titled,
    bordered: displayOptions.bordered,
    theme: normalizeTheme(displayOptions.theme),
    font: displayOptions.font ? "custom" : "instance",
    enabled_download_types: displayOptions.downloads,
    downloads: null, // `downloads` is deprecated, use `enabled_download_types` instead.
  };
}

export const trackPublicLinkCopied = ({
  artifact,
  format = null,
}: {
  artifact: EmbedResourceType;
  format?: ExportFormat | "html" | null;
}): void => {
  trackSchemaEvent(SCHEMA_NAME, {
    event: "public_link_copied",
    artifact,
    format: format as any, // ExportFormatType is untyped
  });
};

export const trackPublicEmbedCodeCopied = ({
  artifact,
  source,
}: {
  artifact: EmbedResourceType;
  source: "public-embed" | "public-share";
}): void => {
  trackSchemaEvent(SCHEMA_NAME, {
    event: "public_embed_code_copied",
    artifact,
    source,
  });
};

export const trackPublicLinkRemoved = ({
  artifact,
  source,
}: {
  artifact: EmbedResourceType;
  source: "public-embed" | "public-share";
}): void => {
  trackSchemaEvent(SCHEMA_NAME, {
    event: "public_link_removed",
    artifact,
    source,
  });
};
